/**
 * Puts 'fast-simon-recommendations.css' inside the shadow root of every Fast
 * Simon "Upsell & Cross Sell" recommendation widget - the only place it can
 * take effect (MKS-346).
 *
 * fs-recommendations-init.js mounts each widget with
 * `element.attachShadow({mode: 'open'})` unless
 * `__fast_options.disable_upsell_shadow_root` is set, so no stylesheet in the
 * document can match the widget heading, its product names or its "add to cart"
 * buttons. That is why the light-DOM rules in custom.css never landed on the
 * PDP widget. Those rules are kept there for the case where the shadow root is
 * disabled, and for the quick view dialog when the app appends it to <body>.
 *
 * Same shape as 'fast-simon-autocomplete-theme.js', with two differences that
 * this widget forces:
 *
 * - adoptedStyleSheets, not an appended <style>. On a product change the app
 *   re-bootstraps with `(element.shadowRoot || element).innerHTML = ''`, which
 *   would delete a <style> child. An adopted sheet is not a child and survives.
 *   Browsers without constructable stylesheets get the <style> and the
 *   per-root observer below re-appends it after each wipe.
 *
 * - The document observer stays connected. There are two hosts on the PDP
 *   (mobile and desktop), the app mounts them lazily on scroll, and it remounts
 *   on variant/product change.
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var href = script && script.dataset.stylesheet;

  if (!href) return;

  var MARKER = 'data-makesy-recommendation-styles';

  /* The class this theme prints (fast-simon-new-upsell-<id>) plus the ones Fast
     Simon's own bundles print for the same element. */
  var HOSTS = [
    'div[class*="fast-simon-new-upsell-"]',
    'div[class*="fast-simon-upsell-"]',
    'div[class*="fast-simon-crossell-widget-"]'
  ].join(', ');

  var canAdopt =
    typeof CSSStyleSheet === 'function' &&
    typeof ShadowRoot === 'function' &&
    'adoptedStyleSheets' in ShadowRoot.prototype &&
    'replaceSync' in CSSStyleSheet.prototype;

  /* Fetched at load, so the text is in hand before the widget scrolls into view
     and the cards never paint a frame in the app's default casing. Resolves to
     null on failure, which switches to a <link>. */
  var cssText = fetch(href)
    .then(function (response) {
      return response.ok ? response.text() : null;
    })
    .catch(function () {
      return null;
    });

  var adoptedSheet = null;
  var watchedRoots = typeof WeakSet === 'function' ? new WeakSet() : null;

  /* Returns true when it fell back to a node in the root, i.e. when the result
     needs watching for the next re-bootstrap. */
  function apply(root, text) {
    if (canAdopt && text) {
      if (!adoptedSheet) {
        adoptedSheet = new CSSStyleSheet();
        adoptedSheet.replaceSync(text);
      }

      if (root.adoptedStyleSheets.indexOf(adoptedSheet) === -1) {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(adoptedSheet);
      }

      return false;
    }

    if (root.querySelector('[' + MARKER + ']')) return true;

    var node;

    if (text) {
      node = document.createElement('style');
      node.textContent = text;
    } else {
      node = document.createElement('link');
      node.rel = 'stylesheet';
      node.href = href;
    }

    node.setAttribute(MARKER, '');
    root.appendChild(node);

    return true;
  }

  /* Only needed on the <style>/<link> path, where a re-bootstrap empties the
     root. Mutations inside a shadow root do not reach an observer on the
     document, so each root needs its own. apply() is idempotent, so the
     append this triggers settles on the next callback. */
  function watch(root) {
    if (!watchedRoots || watchedRoots.has(root)) return;

    watchedRoots.add(root);

    new MutationObserver(function () {
      cssText.then(function (text) {
        apply(root, text);
      });
    }).observe(root, { childList: true });
  }

  function sync() {
    var hosts = document.querySelectorAll(HOSTS);

    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;

      /* No shadow root means the widget is already in the document, where
         custom.css covers it. */
      if (!root) continue;

      (function (shadowRoot) {
        cssText.then(function (text) {
          if (apply(shadowRoot, text)) watch(shadowRoot);
        });
      })(root);
    }
  }

  sync();

  /* attachShadow itself is not observable, but the app appends a zero-width
     space to the host on the same tick to keep `div:empty` rules off it - that
     child is the hook. The subtree is the whole document because the widget's
     parent depends on Fast Simon's own configuration, so the passes are
     coalesced to one per task. A timeout rather than requestAnimationFrame:
     rAF is frozen in a background tab, which is exactly where a widget can
     mount and then be painted the moment the shopper switches to it. */
  var queued = false;

  new MutationObserver(function () {
    if (queued) return;

    queued = true;

    setTimeout(function () {
      queued = false;
      sync();
    }, 0);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
